import {
  ReactNode,
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useRef,
  useMemo,
} from 'react'

import { API_URL } from 'api'
import { useAuth } from 'providers'
import { ConfigState, ConfigUtils, TeachingOrgProps } from 'types'

type ContextType = ConfigUtils

async function getConfig(authToken: string): Promise<ConfigState> {
  const res = await fetch(`${API_URL}/config`, {
    method: 'GET',
    headers: {
      Authorization: `${authToken}`,
    },
  })
  const {
    publicCountries,
    teachingOrgs,
    netsuiteLinkableOrgIds,
  } = await res.json()
  const config = { publicCountries, teachingOrgs, netsuiteLinkableOrgIds }
  return config
}

const ConfigContext = createContext({} as ContextType)

export function ConfigProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth()
  const auth = user?.signInUserSession?.idToken?.jwtToken
  const updatingRef = useRef<boolean>(false)
  const [config, setConfig] = useState<ConfigState | undefined>(undefined)
  const utils = useMemo(
    () => (config ? makeConfigUtils(config) : ({} as ConfigUtils)),
    [config]
  )
  const updateConfig = useCallback(async () => {
    if (auth && !config && !updatingRef.current) {
      try {
        updatingRef.current = true
        const config = await getConfig(auth)
        setConfig(config)
      } catch (e) {
        setConfig(undefined)
      } finally {
        updatingRef.current = false
      }
    }
  }, [setConfig, updatingRef, auth, config])

  useEffect(() => {
    updateConfig()
  }, [updateConfig])

  return (
    <ConfigContext.Provider value={utils}>{children}</ConfigContext.Provider>
  )
}

export const useConfig = () => {
  const { config } = useContext(ConfigContext)
  return config
}
export const useConfigUtils = () => {
  const utils = useContext(ConfigContext)
  if (utils.config) return utils as ConfigUtils
  else return undefined
}

export const makeConfigUtils = (config: ConfigState): ConfigUtils => {
  return {
    config,
    getTeachingOrgs(countryCode) {
      if (!countryCode) return []
      else return config.teachingOrgs[countryCode] ?? []
    },
    getTeachingOrg(countryCode, orgId) {
      if (orgId === undefined) return undefined
      return (
        this.getTeachingOrgs(countryCode).find(
          (org: TeachingOrgProps) => org.id === orgId
        ) ?? undefined
      )
    },
    canLinkToNetSuite(countryCode, orgId) {
      if (!orgId || countryCode !== 'US') {
        return false
      } else return config.netsuiteLinkableOrgIds.includes(orgId)
    },
    isCountryPublic(countryCode) {
      if (!countryCode) return false
      else {
        return !!config.publicCountries.find(
          (publicCountry) => publicCountry.code === countryCode
        )
      }
    },
    isCountryBeta(countryCode) {
      if (!countryCode) return false
      else {
        return !config.publicCountries.find(
          (publicCountry) => publicCountry.code === countryCode
        )
      }
    },
  }
}
